import { useCallback, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import type { CreateObjectDynamicFieldGroup } from 'shared/api/kyrgyzstan/create-object'
import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'

import {
  areCreateObjectDynamicFieldValuesEqual,
  hydrateCreateObjectDynamicFieldValues,
  serializeCreateObjectDynamicFieldValues,
} from '../../lib/dynamic-fields'
import type { CreateObjectDynamicFieldValues } from '../../lib/dynamic-fields'
import type { CreateObjectDraftStepScreenProps } from '../../types'
import { CreateObjectStepLayout } from '../create-object-step-layout'

import { AdditionalAmenitiesStepSkeleton } from './ui/additional-amenities-step-skeleton'
import { DynamicFieldControl } from './ui/dynamic-field-control'

import s from './styles.module.scss'

type AdditionalAmenitiesStepOperation = 'idle' | 'loading' | 'submitting'

export const AdditionalAmenitiesStep = ({
  draftId,
  layoutProps,
  mode = 'create',
  onNext,
}: CreateObjectDraftStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [fieldGroups, setFieldGroups] = useState<
    CreateObjectDynamicFieldGroup[]
  >([])
  const [fieldValues, setFieldValues] =
    useState<CreateObjectDynamicFieldValues>({})
  const [initialFieldValues, setInitialFieldValues] =
    useState<CreateObjectDynamicFieldValues>({})
  const [hasLoadedDynamicFields, setHasLoadedDynamicFields] = useState(false)
  const [operation, setOperation] =
    useState<AdditionalAmenitiesStepOperation>('loading')

  useEffect(() => {
    const abortController = new AbortController()

    const fetchDynamicFields = async () => {
      setHasLoadedDynamicFields(false)
      setOperation('loading')

      try {
        const { data } = await KyrgyzstanCreateObjectApi.getDynamicFieldsStep(
          draftId,
          abortController.signal,
        )

        setFieldGroups(data.data.available_fields)
        const nextFieldValues = hydrateCreateObjectDynamicFieldValues(
          data.data.selected_fields,
        )

        setFieldValues(nextFieldValues)
        setInitialFieldValues(nextFieldValues)
        setHasLoadedDynamicFields(true)
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setOperation('idle')
        }
      }
    }

    fetchDynamicFields()

    return () => {
      abortController.abort()
    }
  }, [draftId, showApiErrorDialog])

  const handleFieldChange = (fieldId: number, value: string) => {
    if (operation !== 'idle' || !hasLoadedDynamicFields) {
      return
    }

    setFieldValues((currentValues) => {
      const nextValues = { ...currentValues }

      if (!value.trim()) {
        delete nextValues[fieldId]
      } else {
        nextValues[fieldId] = value
      }

      return nextValues
    })
  }

  const handleContinue = useCallback(async () => {
    if (operation !== 'idle' || !hasLoadedDynamicFields) {
      return
    }

    setOperation('submitting')
    let shouldGoNext = false

    try {
      await KyrgyzstanCreateObjectApi.saveDynamicFieldsStep(
        draftId,
        serializeCreateObjectDynamicFieldValues(fieldValues, fieldGroups),
      )

      shouldGoNext = true
    } catch (error) {
      showApiErrorDialog(error)
    } finally {
      setOperation('idle')
    }

    if (shouldGoNext) {
      onNext()
    }
  }, [
    draftId,
    fieldGroups,
    fieldValues,
    hasLoadedDynamicFields,
    onNext,
    operation,
    showApiErrorDialog,
  ])

  const hasLocalChanges = !areCreateObjectDynamicFieldValuesEqual(
    fieldValues,
    initialFieldValues,
    fieldGroups,
  )
  const isEditModePristine = mode === 'edit' && !hasLocalChanges
  const isSubmitDisabled =
    !hasLoadedDynamicFields || operation !== 'idle' || isEditModePristine
  const isControlDisabled = operation !== 'idle' || !hasLoadedDynamicFields

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      continueDisabled={isSubmitDisabled}
      continueLabel={
        layoutProps.continueLabel ??
        t('kyrgyzstan_block.create_object.actions.save')
      }
      continueLoading={operation === 'submitting'}
      onContinue={handleContinue}
      title={t(
        'kyrgyzstan_block.create_object.steps.additional_amenities.title',
      )}
    >
      <div className={clsx(s.container, 'container')}>
        {operation === 'loading' ? (
          <AdditionalAmenitiesStepSkeleton />
        ) : (
          fieldGroups.map((fieldGroup) => (
            <div
              key={fieldGroup.id}
              className={s.section}
            >
              <p className={clsx(s.label, 'bold-16')}>{fieldGroup.name}</p>

              {fieldGroup.fields.map((field) => (
                <DynamicFieldControl
                  key={field.id}
                  field={field}
                  value={fieldValues[field.id] ?? ''}
                  disabled={isControlDisabled}
                  onChange={handleFieldChange}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </CreateObjectStepLayout>
  )
}
